import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { useAdminQueries } from '../hooks/useAdminQueries';
import { api } from '../../../../core/utils/apiClient';
import { QueryDetailsDrawer } from '../components/QueryDetailsDrawer';
import { AdminQuery } from '../types';
import { COLORS } from '../../../../core/constants/colors';

export const AdminQueriesPage: React.FC = () => {
  const { queries: initialQueries, isLoading } = useAdminQueries();
  const [queries, setQueries] = useState<AdminQuery[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [showQueryDetails, setShowQueryDetails] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<AdminQuery | null>(null);
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null);
  const hasLoadedQueries = useRef(false);

  // Sync queries from hook to local state (only on initial load)
  useEffect(() => {
    if (!hasLoadedQueries.current && initialQueries.length > 0) {
      setQueries(initialQueries);
      hasLoadedQueries.current = true;
    }
  }, [initialQueries]);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/v1/companies/');
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleViewQuery = (query: AdminQuery) => {
    setSelectedQuery(query);
    setSelectedQueryId(query.id);
    setShowQueryDetails(true);
  };

  // Calculate analytics
  const analytics = {
    totalQueries: queries.length,
    successfulQueries: queries.filter(q => q.status === 'success').length,
    failedQueries: queries.filter(q => q.status === 'failed' || q.status === 'error').length,
    avgResponseTime: queries
      .filter(q => q.response_time_ms)
      .reduce((acc, q) => acc + q.response_time_ms!, 0) / 
      queries.filter(q => q.response_time_ms).length || 0,
    successRate: queries.length > 0 
      ? ((queries.filter(q => q.status === 'success').length / queries.length) * 100).toFixed(1)
      : '0',
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, { bg: string; text: string }> = {
      success: { bg: '#D1FAE5', text: '#059669' },
      failed: { bg: '#FEE2E2', text: '#DC2626' },
      error: { bg: '#FEE2E2', text: '#DC2626' },
    };
    return statusColors[status] || { bg: '#F3F4F6', text: '#6B7280' };
  };

  if (isLoading) {
    return (
      <AdminLayout pageTitle="Queries" pageDescription="Manage and monitor all AI queries">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading queries...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Queries" pageDescription="Manage and monitor all AI queries">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="w-full px-8 py-8 overflow-x-hidden">
          {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Total Queries</p>
              <p className="text-2xl font-bold" style={{ color: '#1F2937' }}>
              {analytics.totalQueries}
            </p>
          </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Success Rate</p>
              <p className="text-2xl font-bold" style={{ color: '#059669' }}>
              {analytics.successRate}%
            </p>
          </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Avg Response Time</p>
              <p className="text-2xl font-bold" style={{ color: '#1F2937' }}>
              {Math.round(analytics.avgResponseTime)}ms
            </p>
          </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Failed Queries</p>
              <p className="text-2xl font-bold" style={{ color: '#DC2626' }}>
              {analytics.failedQueries}
            </p>
          </div>
        </div>

          {/* Queries List */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            {queries.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm" style={{ color: '#6B7280' }}>No queries yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-6 py-3 text-xs font-medium" style={{ color: '#6B7280' }}>Query</th>
                    <th className="text-left px-6 py-3 text-xs font-medium" style={{ color: '#6B7280' }}>Company</th>
                    <th className="text-left px-6 py-3 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
                    <th className="text-left px-6 py-3 text-xs font-medium" style={{ color: '#6B7280' }}>Response Time</th>
                    <th className="text-left px-6 py-3 text-xs font-medium" style={{ color: '#6B7280' }}>Date</th>
                    <th className="text-right px-6 py-3 text-xs font-medium" style={{ color: '#6B7280' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {queries.map((query) => {
                    const statusStyle = getStatusBadge(query.status);
                    const companyName = companies.find(c => c.id === query.company_id)?.name || 'All Companies';
                    return (
                      <tr key={query.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium truncate max-w-xs" style={{ color: '#1F2937' }}>
                            {query.query_text}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm" style={{ color: '#6B7280' }}>{companyName}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-2 py-1 text-xs font-medium rounded-full"
                            style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                          >
                            {query.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm" style={{ color: '#6B7280' }}>
                            {query.response_time_ms ? `${query.response_time_ms}ms` : '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm" style={{ color: '#6B7280' }}>
                            {new Date(query.created_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleViewQuery(query)}
                            className="text-sm font-medium hover:underline"
                            style={{ color: COLORS.admin.primary }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Query Details Drawer */}
      <QueryDetailsDrawer
        queryId={selectedQueryId}
        query={selectedQuery}
        isOpen={showQueryDetails}
        onClose={() => {
          setShowQueryDetails(false);
          setSelectedQuery(null);
          setSelectedQueryId(null);
        }}
      />
    </AdminLayout>
  );
};